import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEntraIdentity } from '@hooks/useEntraIdentity';
import { GridOperatorContext } from '@components/grid/useGridView';
import { Header } from '@components/layout/Header';
import { Nav, type NavGroup } from '@components/layout/Nav';
import { Footer } from '@components/layout/Footer';
import { FreshnessBanner } from '@components/layout/FreshnessBanner';
import { FreshnessProvider, useFreshness } from '@lib/freshness';
import { SmsInboundProvider } from '@lib/sms-inbound';
import { openCountWords, useInboundOpenCount } from '@lib/inbound-open-count';
import { clientDef, loadClientList, setCurrentClient, useClient, useClientHint, useClientList, useFeatures } from '@lib/clients';
import { ALL, CAMPAIGN_MENU_WORDING, loadCampaignList, setCurrentCampaign, useCampaignState } from '@lib/campaigns';
import { masterReadback } from '@lib/care-api';
import { cn } from '@/lib/cn';
import { OPEN_MODES, OPEN_MODE_WORDING, setOpenMode, useOpenMode, type OpenMode } from '@lib/open-mode';

/** PL-009 K4: how often the open console reads the master's batches back (the door applies in about a minute). */
export const READBACK_EVERY_MS = 2 * 60 * 1000;

/**
 * AppShell - the ClariCommand editorial frame for Member Care SP. Keeps the react-router spine:
 * the left nav drives navigate(), the active item is derived from the current path, and page content
 * renders through <Outlet/>. The header renders the operator's name + email (rev3 multi-operator
 * identity). The freshness banner reflects the live queue-refresh state via FreshnessContext.
 */

// External destinations open in a new tab (see onNavigate below); they never highlight as active.
const COMMUNICATE_URL = 'https://cdx-membercare.mangoglacier-22711908.westus2.azurecontainerapps.io';
const CLAIRE_URL = 'https://cdx-claire-dashboard.nicerock-b8b041d4.westus2.azurecontainerapps.io/';

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Member Care',
    items: [
      // 02 Calling added 2026-07-09; the trailing items renumbered (num is display-only, ids are paths).
      { id: '/', num: '01', name: 'Inbound queue', meta: 'Flow A' },
      // Row 400 (approval 193): the Opt In lane, nested under the Inbound queue. Shown only when the
      // deployment offers the reply groups (features.replyButtons); the page is dark otherwise.
      // William, 16 Oct 2026 8:45 PM, of the seven lines that stood here (01A New Opt In to 01G Help):
      // "Remove all of these items - just one master queue. Just confirm that all of the inbound will be in
      // the main queue." Each was a browser-side filter of the same list 01 reads, so 01 holds every entry.
      // The page stays reachable by its address (/inbound/opt-in), with no menu line.
      { id: '/calling', num: '02', name: 'Calling', meta: 'Worklist' },
      { id: '/live-call', num: '03', name: 'Live call', meta: 'Flow B' },
      { id: '/final-review', num: '04', name: 'Final review', meta: 'QA' },
      { id: '/lookup', num: '05', name: 'Lookup', meta: 'Records' },
      { id: COMMUNICATE_URL, num: '06', name: 'Communicate', meta: 'External' },
      { id: CLAIRE_URL, num: '07', name: 'Claire', meta: 'External' },
      { id: '/archive', num: '08', name: 'Archive', meta: 'History' },
      // 09 Reports (Alignment feedback 2026-08): client-facing reports off the worked queue. The
      // meta follows the client picker (filled in per render, see Shell).
      { id: '/reports', num: '09', name: 'Reports', meta: '' },
    ],
  },
  {
    // ROW 325 (William, 17 Sep 2026 8:07 PM, his OneNote note "Care - Other Exceptions"): "put a
    // seperator line below Reports and Call is Exceptions and then have 10 Opt Outs, etc., Also,
    // can you add another tab called 13 Technical Exceptions." The group label IS the heading, and
    // the rule above it is the separator (index.css, .side-group + .side-group).
    label: 'Exceptions',
    items: [
      // 10 Opt-outs (William 14 Sep 2026): the STOP review table, and since row 325 the one-campaign
      // opt-out list above it. Its own screen, because it WRITES a decision with an audit row.
      { id: '/optouts', num: '10', name: 'Opt-outs', meta: 'Campaign' },
      { id: '/usps-rejections', num: '11', name: 'USPS rejections', meta: 'Mail' },
      // 12 Do not contact (William 17 Sep 2026, row 286): the DNC master table Marny reviews, adjusts
      // and removes from. Shown only when the API says the deployment offers it (dark otherwise).
      { id: '/dnc', num: '12', name: 'Do not contact', meta: 'Master' },
      // 13 Technical exceptions (row 325): everything else that stops contact, on the same master.
      { id: '/technical', num: '13', name: 'Technical exceptions', meta: 'Other' },
    ],
  },
];

/**
 * Row 410 (William, 19 Sep 2026; approval 208): the Video (test) tab, its own group after Exceptions.
 * Rendered ONLY when the sign-in read said the flag is on (`test` or `on`) AND this operator is on the
 * test list; NAV_GROUPS above stays as it is, and this group is appended per render (see Shell).
 */
const VIDEO_TEST_GROUP: NavGroup = {
  label: 'Testing',
  items: [{ id: '/video-test', num: '14', name: 'Video (test)', meta: 'Test' }],
};

/** The two screens that exist only on a deployment offering the master list (MEMBERCARE_DNC_MASTER). */
const MASTER_ONLY = new Set(['/dnc', '/technical']);

function activeIdFor(pathname: string): string {
  // 16 Oct 2026 8:45 PM: the Opt In page has no menu line any more (one master queue); its address still
  // works and highlights nothing, as Consent chase below does.
  if (pathname.startsWith('/inbound/opt-in')) return '/inbound/opt-in';
  if (pathname.startsWith('/calling')) return '/calling';
  if (pathname.startsWith('/live-call')) return '/live-call';
  // Consent chase was de-navved 2026-07-06 (proactive lane parked); the URL stays reachable but
  // maps to no nav item, so nothing highlights while it renders.
  if (pathname.startsWith('/consent-chase')) return '/consent-chase';
  if (pathname.startsWith('/final-review')) return '/final-review';
  if (pathname.startsWith('/archive')) return '/archive';
  if (pathname.startsWith('/reports')) return '/reports';
  if (pathname.startsWith('/dnc')) return '/dnc';
  if (pathname.startsWith('/technical')) return '/technical';
  if (pathname.startsWith('/video-test')) return '/video-test';
  if (pathname.startsWith('/optouts')) return '/optouts';
  if (pathname.startsWith('/usps-rejections')) return '/usps-rejections';
  // rev6: /pcp-lookup redirects into the Lookup PCP tab; both paths highlight nav 04.
  if (pathname.startsWith('/pcp-lookup')) return '/lookup';
  if (pathname.startsWith('/lookup')) return '/lookup';
  return '/';
}

export function AppShell() {
  // Keyed on the selected client: a switch remounts the whole frame (queue polls, the SMS unread
  // state, the Inbound queue's open count, every page's loaders), so nothing rendered for one client
  // survives into the other.
  const client = useClient();
  return (
    <FreshnessProvider key={client}>
      <SmsInboundProvider>
        <Shell />
      </SmsInboundProvider>
    </FreshnessProvider>
  );
}

/**
 * The Workflow Hub can hand this console a `?client=<slug>` preselection hint. When it could NOT be
 * honoured, the Hub's URL contract (2026-09-04, section 1.3) requires the screen to SAY SO and name
 * the client it is showing instead: "silence here is the failure mode the whole plan is written
 * against". A hint that WAS honoured needs no notice, because the CLIENT chip already shows it.
 */
function ClientHintNotice({ client }: { client: string }) {
  const [dismissed, setDismissed] = useState(false);
  const hint = useClientHint();
  // `pending` is the moment between landing and the list read; it settles into applied, unknown or
  // unverifiable and needs no words of its own.
  if (dismissed || hint.kind === 'none' || hint.kind === 'applied' || hint.kind === 'pending') return null;
  // Three outcomes, not two (2026-09-04): "not on the list" and "could not check the list" are
  // different statements, and the second must never be worded as the first.
  const what =
    hint.kind === 'repeated'
      ? 'The link named more than one client, so it was ignored.'
      : hint.kind === 'unverifiable'
        ? `The link named a client ("${hint.value}") that could not be verified: the client list could not be read (${hint.error}). It was not applied.`
        : `The link named an unknown client ("${hint.value}"), so it was ignored.`;
  return (
    <div
      role="status"
      data-testid="client-hint-notice"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        background: '#fff4e5',
        borderBottom: '1px solid #f0c98a',
        color: '#7a4f00',
        font: '12px/1.4 inherit',
      }}
    >
      <span>
        {what} Showing <strong>{clientDef(client).displayName}</strong>.
      </span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', font: 'inherit' }}
      >
        Dismiss
      </button>
    </div>
  );
}

/**
 * ROW 354: the CAMPAIGN chip, beside CLIENT and after it (his order for the Sequencer, row 334). One
 * <select> over the list READ for the client in force, "All campaigns" first; remembered per client.
 */
function CampaignPicker() {
  const s = useCampaignState();
  return (
    <select
      aria-label="Campaign"
      data-testid="campaign-select"
      value={s.selected}
      onChange={(e) => setCurrentCampaign(e.target.value)}
      style={{
        background: 'transparent',
        color: 'inherit',
        border: '1px solid rgba(255,255,255,0.28)',
        borderRadius: 4,
        padding: '1px 4px',
        font: 'inherit',
        cursor: 'pointer',
      }}
    >
      <option value={ALL} style={{ color: '#111' }}>{CAMPAIGN_MENU_WORDING.all}</option>
      {s.list.map((c) => (
        <option key={c.slug} value={c.slug} style={{ color: '#111' }}>
          {c.label}
        </option>
      ))}
      {/* A remembered choice the list has not confirmed yet still shows as itself, never as blank. */}
      {s.selected !== ALL && !s.list.some((c) => c.slug === s.selected) && (
        <option value={s.selected} style={{ color: '#111' }}>{s.selected}</option>
      )}
    </select>
  );
}

/** Said, never silent: a list that could not be read, or a remembered campaign no longer running. */
function CampaignNotice() {
  const s = useCampaignState();
  const text = s.notice || (s.loaded && s.source === 'none' && s.reason ? CAMPAIGN_MENU_WORDING.unreadable(s.reason) : '');
  if (!text) return null;
  return <div className="note micro" role="status" style={{ margin: '6px 16px 0' }}>{text}</div>;
}

/** Each chip's words and one-line help, by mode (lib/open-mode.ts holds the words). */
const OPEN_MODE_CHIP: Record<OpenMode, { label: string; help: string }> = {
  below: { label: OPEN_MODE_WORDING.below, help: OPEN_MODE_WORDING.belowHelp },
  side: { label: OPEN_MODE_WORDING.side, help: OPEN_MODE_WORDING.sideHelp },
  popout: { label: OPEN_MODE_WORDING.popout, help: OPEN_MODE_WORDING.popoutHelp },
};

/**
 * OPEN CASES IN (William, 2 Oct 2026): "put a switch at the top of the portal - pop out or side panel."
 * Then, the same night: "yes - give her three choices as a default presentation." Three chips in his
 * order, Under the row, Side panel, Pop out (OPEN_MODES). One choice for every case opened from a row on
 * any page (lib/open-mode.ts), remembered per browser; the side panel is the default. Styled as the
 * Inbound queue's view chips.
 */
function OpenModeSwitch() {
  const mode = useOpenMode();
  return (
    <span
      role="group"
      aria-label={OPEN_MODE_WORDING.label}
      data-testid="open-mode-switch"
      style={{ display: 'inline-flex', gap: 4, verticalAlign: 'middle' }}
    >
      {OPEN_MODES.map((m) => (
        <button
          key={m}
          type="button"
          className={cn('chip', mode === m && 'selected')}
          aria-pressed={mode === m}
          title={OPEN_MODE_CHIP[m].help}
          data-testid={`open-mode-${m}`}
          onClick={() => setOpenMode(m)}
        >
          {OPEN_MODE_CHIP[m].label}
        </button>
      ))}
    </span>
  );
}

/** The CLIENT chip is the picker itself: one <select> over the list in force, persisted per browser. */
function ClientPicker({ client }: { client: string }) {
  const clients = useClientList();
  return (
    <select
      aria-label="Client"
      data-testid="client-select"
      value={client}
      onChange={(e) => setCurrentClient(e.target.value)}
      style={{
        background: 'transparent',
        color: 'inherit',
        border: '1px solid rgba(255,255,255,0.28)',
        borderRadius: 4,
        padding: '1px 4px',
        font: 'inherit',
        cursor: 'pointer',
      }}
    >
      {clients.map((c) => (
        <option key={c.slug} value={c.slug} style={{ color: '#111' }}>
          {c.displayName}
        </option>
      ))}
    </select>
  );
}

function Shell() {
  const identity = useEntraIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, label } = useFreshness();
  // Row 866: the Inbound queue's open count, read from the page's own answers (null: show nothing).
  const openCount = useInboundOpenCount();
  const client = useClient();
  const activeId = activeIdFor(location.pathname);
  // The client list is READ from the API once signed in (Wave 2): the registry when loaded, the
  // compiled floor before. Until it lands the floor is in force, so the picker is never empty.
  useEffect(() => {
    void loadClientList();
  }, []);
  // Row 354: the campaign list is per client, so it is read again whenever the client changes.
  useEffect(() => {
    void loadCampaignList();
  }, [client]);

  // Row 866 (William, 4 Oct 2026: "Show the real count"): badge the Inbound item with the number of
  // OPEN entries the Inbound queue lists by default, in place of the SMS unread count it carried (the
  // numbers that had texted since this browser started watching: thousands, and not the queue). Label
  // Reports with the selected client's short name.
  const features = useFeatures();
  // PL-009 K4 (Care's 1:03 PM point 2): while presses go to the master's door, the open console reads their
  // batches back every two minutes, so a batch the door refuses after accepting it puts its entry back in
  // the queue with a note. Off, nothing is called.
  useEffect(() => {
    if (!features.masterDecisions) return undefined;
    const run = () => { void masterReadback().catch(() => undefined); };
    run();
    const t = setInterval(run, READBACK_EVERY_MS);
    return () => clearInterval(t);
  }, [features.masterDecisions, client]);
  const navGroups = NAV_GROUPS.map((g) => ({
    ...g,
    // Row 286: nav 12 (and row 325's 13) exist only on a deployment that offers them; the URL still
    // resolves (the page says the table is not enabled), but nothing in the nav points at a dark screen.
    items: g.items
      .filter((it) => !MASTER_ONLY.has(it.id) || features.dncMaster)
      .map((it) => {
      if (it.id === '/' && openCount !== null && openCount > 0) {
        return { ...it, badge: openCount, badgeTitle: openCountWords(openCount) };
      }
      if (it.id === '/reports') return { ...it, meta: clientDef(client).shortName };
      return it;
    }),
  }));
  // Row 410: nav 14 exists only for an operator on the test list while the flag is on.
  const showVideoTest = features.videoLinks !== 'off' && features.videoTestAllowed;
  const navGroupsShown = showVideoTest ? [...navGroups, VIDEO_TEST_GROUP] : navGroups;

  return (
    <div
      className="app-root"
      style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--paper)' }}
    >
      <a
        href="#main-content"
        className="btn btn-sm"
        style={{ position: 'absolute', left: -9999, top: 8, zIndex: 100 }}
        onFocus={(e) => {
          e.currentTarget.style.left = '8px';
        }}
        onBlur={(e) => {
          e.currentTarget.style.left = '-9999px';
        }}
      >
        Skip to content
      </a>
      <Header
        brand={{ mark: 'M', name: 'Member Care' }}
        meta={[{ label: 'CLIENT', value: <ClientPicker client={client} /> }, { label: 'CAMPAIGN', value: <CampaignPicker /> }, { label: OPEN_MODE_WORDING.label, value: <OpenModeSwitch /> }]}
        role="OPERATOR"
        user={identity ? { name: identity.name, email: identity.email, initials: identity.initials } : undefined}
      />
      <ClientHintNotice client={client} />
      <CampaignNotice />
      <FreshnessBanner state={state} label={label || 'Queue live'} />

      <div className="app-body" style={{ display: 'flex', flex: '1 1 auto', minHeight: 0 }}>
        <Nav
          groups={navGroupsShown}
          activeId={activeId}
          onNavigate={(id) => {
            // External links (http/https ids) open in a new tab so the operator keeps the console;
            // internal route ids go through react-router.
            if (/^https?:\/\//i.test(id)) {
              window.open(id, '_blank', 'noopener,noreferrer');
              return;
            }
            navigate(id);
          }}
        />
        <main
          id="main-content"
          tabIndex={-1}
          className="app-main"
          style={{ flex: '1 1 auto', minWidth: 0, overflowX: 'auto', background: 'var(--paper)' }}
        >
          {/* Row 356: every table remembers its layout per signed-in operator. */}
          <GridOperatorContext.Provider value={identity?.email ?? ''}>
            <Outlet />
          </GridOperatorContext.Provider>
        </main>
      </div>

      <Footer left="Member Care SP · ClariDynamics" right="Cache-Control: no-store" />
    </div>
  );
}
